import { useQuery } from "@tanstack/react-query";
import { supabase } from "../providers/supabase/supabase";

/**
 * Unidades (escritórios/filiais) da organização ativa (migration 308). Quem vê
 * o quê é do banco: aqui só a leitura e a gravação que as telas usam.
 */

export interface Unidade {
  id: string;
  nome: string;
  cidade: string | null;
  ativa: boolean;
}

export interface Lotacao {
  unidade_id: string | null;
  unidade_nome: string | null;
  administrador: boolean;
  responsavel: boolean;
  /** Unidade escolhida pelo administrador no topo; null = todas. */
  filtro: string | null;
  filtro_nome: string | null;
  unidades_ativas: number;
}

type Resposta<T> = Promise<{ data: T | null; error: { message: string; code?: string } | null }>;

const rpc = <T,>(nome: string, args?: Record<string, unknown>) =>
  supabase.rpc(nome as never, args as never) as unknown as Resposta<T>;

const tabela = () => supabase.from("unidades" as never);

/** Mensagem do banco quando é de validação; senão a genérica. */
const mensagem = (error: { message: string; code?: string }, padrao: string) =>
  error.code && ["22023", "42501", "P0002", "23505"].includes(error.code)
    ? error.code === "23505" ? "Já existe uma unidade com este nome." : error.message
    : padrao;

export async function listarUnidades(): Promise<Unidade[]> {
  const { data, error } = (await tabela()
    .select("id, nome, cidade, ativa")
    .order("nome" as never)) as unknown as { data: Unidade[] | null; error: { message: string } | null };
  if (error) throw new Error("Não foi possível carregar as unidades.");
  return data ?? [];
}

export interface LinhaUnidade extends Unidade {
  pessoas: number;
}

/** Tela Unidades: só super admin e administrador recebem linhas. */
export async function painelUnidades(): Promise<LinhaUnidade[]> {
  const { data, error } = await rpc<LinhaUnidade[]>("unidades_painel");
  if (error) throw new Error("Não foi possível carregar as unidades.");
  return data ?? [];
}

export async function salvarUnidade(u: { id: string | null; nome: string; cidade: string; ativa: boolean }) {
  const linha = { nome: u.nome.trim(), cidade: u.cidade.trim() || null, ativa: u.ativa };
  const { error } = (u.id
    ? await tabela().update(linha as never).eq("id" as never, u.id as never)
    : await tabela().insert(linha as never)) as unknown as { error: { message: string; code?: string } | null };
  if (error) throw new Error(mensagem(error, "Não foi possível salvar a unidade."));
}

export async function minhaLotacao(): Promise<Lotacao | null> {
  const { data, error } = await rpc<Lotacao[]>("minha_lotacao");
  if (error) return null;
  return data?.[0] ?? null;
}

export async function verUnidade(id: string | null) {
  const { error } = await rpc<string>("ver_unidade", { p_unidade: id });
  if (error) throw new Error(mensagem(error, "Não foi possível trocar de unidade."));
}

export async function definirLotacao(crmUserId: number, unidadeId: string | null, gerenteId: number | null) {
  const { error } = await rpc<null>("definir_lotacao", {
    p_crm_user_id: crmUserId,
    p_unidade: unidadeId,
    p_manager_id: gerenteId,
  });
  if (error) throw new Error(mensagem(error, "Não foi possível salvar unidade e gerente."));
}

export async function papeisQuePossoDar(): Promise<string[]> {
  const { data } = await rpc<unknown[]>("papeis_que_posso_dar");
  return (data ?? []).map((p) =>
    typeof p === "string" ? p : String((p as { papeis_que_posso_dar?: string }).papeis_que_posso_dar ?? ""),
  ).filter(Boolean);
}

export const useLotacao = (habilitado = true) =>
  useQuery({
    queryKey: ["unidades", "lotacao"],
    enabled: habilitado,
    staleTime: 5 * 60_000,
    queryFn: minhaLotacao,
  });

export const useUnidades = (habilitado = true) =>
  useQuery({
    queryKey: ["unidades", "lista"],
    enabled: habilitado,
    staleTime: 5 * 60_000,
    queryFn: listarUnidades,
  });

export const ROTULO_PAPEL: Record<string, string> = {
  super_admin: "Super Administrador",
  admin_organizacao: "Administrador",
  gerente_unidade: "Responsável pela unidade",
  manager: "Gerente",
  user: "Usuário",
};
